import React, { useEffect, useState } from "react";
import {
  EyeIcon,
  EyeOffIcon,
  KeyIcon,
  MailIcon,
  LockClosedIcon, // Added for visual flair
} from "@heroicons/react/outline";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { loginRequest, clearLoginSuccess } from "../../redux/action";
import toast from "react-hot-toast";
import { RegisterButton } from "../Buttons";
import { FormError } from "./FormError";

export const LoginForm = () => {
  const [passType, setPassType] = useState(true);
  const history = useHistory();
  const dispatch = useDispatch();

  const { loginSuccess, loginError, loginLoading } = useSelector((state) => ({
    loginSuccess: state.ajaxStatuses.success.login,
    loginError: state.ajaxStatuses.errors.login,
    loginLoading: state.loadingIndicator.loginLoading,
  }));

  const formik = useFormik({
    initialValues: {
      usernameOrEmail: "",
      password: "",
    },
    validationSchema: yup.object({
      usernameOrEmail: yup.string().required("Email or username is required"),
      password: yup.string().required("Password is required"),
    }),
    onSubmit: (values) => {
      dispatch(loginRequest(values));
    },
  });

  useEffect(() => {
    if (loginError) toast.error(loginError);
  }, [loginError]);

  useEffect(() => {
    if (loginSuccess) {
      toast.success("Welcome back!");
      history.push("/dashboard");
      dispatch(clearLoginSuccess());
    }
  }, [loginSuccess, history, dispatch]);

  return (
    <div className="flex items-center justify-center py-10 px-2 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-6 md:p-8 rounded-2xl shadow-xl border border-gray-100">
        {/* Header Section */}
        <div className="text-center">
          <h2 className="text-3xl font-heading font-bold text-gray-900 tracking-tight">
            Welcome Back
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            Sign in to manage your portfolio
          </p>
        </div>

        {/* Form Section */}
        <form className="mt-8 space-y-6" onSubmit={formik.handleSubmit}>
          <div className="space-y-5">
            {/* Email Input */}
            <div>
              <label
                htmlFor="email"
                className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2"
              >
                Email Address
              </label>
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MailIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </div>
                <input
                  id="email"
                  name="usernameOrEmail"
                  type="email"
                  autoComplete="email"
                  className={`
                    block w-full pl-10 pr-3 py-3 border rounded-xl leading-5 placeholder-gray-400 
                    focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-all duration-200
                    ${
                      formik.errors.usernameOrEmail &&
                      formik.touched.usernameOrEmail
                        ? "border-red-300 bg-red-50 text-red-900 focus:ring-red-500"
                        : "border-gray-200 bg-gray-50 focus:bg-white text-gray-900"
                    }
                  `}
                  placeholder="name@example.com"
                  {...formik.getFieldProps("usernameOrEmail")}
                />
              </div>
              {formik.errors.usernameOrEmail &&
                formik.touched.usernameOrEmail && (
                  <div className="mt-1">
                    <FormError message={formik.errors.usernameOrEmail} />
                  </div>
                )}
            </div>

            {/* Password Input */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label
                  htmlFor="password"
                  className="block text-xs font-bold text-gray-700 uppercase tracking-wide"
                >
                  Password
                </label>

                <Link
                  to="/forgot-password"
                  className="text-xs font-medium text-primary-600 hover:text-primary-500"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <KeyIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </div>
                <input
                  id="password"
                  name="password"
                  type={passType ? "password" : "text"}
                  autoComplete="current-password"
                  className={`
                    block w-full pl-10 pr-10 py-3 border rounded-xl leading-5 placeholder-gray-400 
                    focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-all duration-200
                    ${
                      formik.errors.password && formik.touched.password
                        ? "border-red-300 bg-red-50 text-red-900 focus:ring-red-500"
                        : "border-gray-200 bg-gray-50 focus:bg-white text-gray-900"
                    }
                  `}
                  placeholder="••••••••"
                  {...formik.getFieldProps("password")}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    type="button"
                    onClick={() => setPassType(!passType)}
                    className="text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
                    aria-label={passType ? "Hide password" : "Show password"}
                  >
                    {passType ? (
                      <EyeIcon className="h-5 w-5" aria-hidden="true" />
                    ) : (
                      <EyeOffIcon className="h-5 w-5" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>
              {formik.errors.password && formik.touched.password && (
                <div className="mt-1">
                  <FormError message={formik.errors.password} />
                </div>
              )}
            </div>
          </div>

          <div className="pt-2">
            <RegisterButton
              loading={loginLoading}
              disable={!(formik.dirty && formik.isValid)}
              action={formik.handleSubmit}
              text={"Sign In"}
            />
          </div>
        </form>

        {/* Footer */}

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">
                New to the platform?
              </span>
            </div>
          </div>

          <div className="mt-6 grid gap-3">
            <Link
              to="/register"
              className="w-full inline-flex justify-center py-3 px-4 border border-gray-200 rounded-xl shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 transition-colors"
            >
              Create an account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
